import React, { Fragment, useContext, useEffect, useState } from 'react';
import './SectionPageBody.scss';
import { useParams } from "react-router-dom";
import { getSectionsForYear } from "root/DataFunctions/SectionFns";

import { getDataFromResponse } from "root/DataFunctions/BaseDataFns";
import SpinnerDiv from "root/Forms/UI/SpinnerDiv";

import useModalCompleteAudit from "root/Modals/useModalCompleteAudit";
import SectionLink from "root/Questions/SectionLink";
import ContainerButton from "root/ContainerButton";
import { ApplicationFeatures, AuditReviewStatus } from "root/DataFunctions/Constants";
import GlobalContext, { useGlobalContext } from "root/Contexts/GlobalContext";
import { ModalCompleteAuditMode } from "root/Modals/ModalCompleteAudit";

const SectionPageSurvey = ( { survey_id, academic_year } ) =>
{
    const [ data, setData ] = useState ( { isLoading: true, data: [] } );
    const [ renderModal, activateModal ] = useModalCompleteAudit ();

    let { provider_id } = useParams ();

    const sessionContext = useContext ( GlobalContext );

    const survey_type = sessionContext.getCurrentSurveyType ();

    const { isFeatureEnabled } = useGlobalContext ();

    useEffect ( () =>
    {
        getData ();
    }, [] );

    const getData = () =>
    {

        const apiProviderId = provider_id === undefined ? 'current' : encodeURIComponent ( provider_id );

        getSectionsForYear ( apiProviderId, survey_id, academic_year, ( response ) =>
        {
            const responseData = getDataFromResponse ( response );

            setData ( { isLoading: false, data: responseData } );
        } );
    };

    const onDialogClosed = ( result ) =>
    {
        if ( result === 'saved' )
        {
            getData ();
        }
    }

    const onClickUpload = () =>
    {
        const rStatus = getReviewStatus ();

        const provider_table_instance_id = data.data[ 0 ].provider_table_instance_id;

        if ( ( rStatus === AuditReviewStatus.NOT_SUBMITTED ) || ( rStatus === AuditReviewStatus.IN_REVIEW ) )
        {
            activateModal ( encodeURIComponent ( provider_id ), survey_type, provider_table_instance_id, rStatus, ModalCompleteAuditMode.SUBMIT, onDialogClosed );
        }
        else if ( rStatus === AuditReviewStatus.REQUIRES_REVIEW )
        {
            activateModal ( encodeURIComponent ( provider_id ), survey_type, provider_table_instance_id, rStatus, ModalCompleteAuditMode.REOPEN, onDialogClosed );
        }
        else if ( rStatus === AuditReviewStatus.SUBMITTED || rStatus === AuditReviewStatus.REVIEW_SUBMITTED )
        {
            activateModal ( encodeURIComponent ( provider_id ), survey_type, provider_table_instance_id, rStatus, ModalCompleteAuditMode.ADHOC_REOPEN, onDialogClosed );
        }
    }

    const isComplete = () =>
    {
        const instance = data.data[ 0 ];

        const is = !( instance.date_completed === null );

        return is;
    }

    const isReviewRequired = () =>
    {
        const instance = data.data[ 0 ];

        const is = !( instance.review_due_date === null ) && !isReviewCompleted ();

        return is;
    }

    const isReviewStarted = () =>
    {
        const instance = data.data[ 0 ];

        const is = !( instance.review_started_date === null );

        return is;
    }

    const isReviewCompleted = () =>
    {
        const instance = data.data[ 0 ];

        const is = !( instance.review_completed_date === null );

        return is;
    }

    const getReviewStatus = () =>
    {
        let currentStatus = AuditReviewStatus.NOT_SUBMITTED;

        if ( isComplete () )
        {
            if ( isReviewRequired () )
            {
                if ( isReviewStarted () )
                {
                    currentStatus = AuditReviewStatus.IN_REVIEW;
                }
                else
                {
                    currentStatus = AuditReviewStatus.REQUIRES_REVIEW;
                }
            }
            else
            {
                if ( isReviewCompleted () )
                {
                    currentStatus = AuditReviewStatus.REVIEW_SUBMITTED;
                }
                else
                {
                    currentStatus = AuditReviewStatus.SUBMITTED;
                }
            }
        }

        return currentStatus;
    }

    const getAcademicYear = () =>
    {
        const instance = data.data[ 0 ];

        const yr = +( instance.academic_year );
        return yr;
    }

    const sectionHeading = () =>
    {
        const instance = data.data[ 0 ];

        const yr = getAcademicYear ();
        const academicYearRangeStr = yr + ' - ' + ( yr + 1 );

        const reviewStatus = getReviewStatus ();

        switch ( reviewStatus )
        {
            case AuditReviewStatus.SUBMITTED:
                return (
                    <div className='new-info-row'>
                        <p style={ { textAlign: 'center' } }>This audit report is for the academic year { academicYearRangeStr }.
                            The report has been completed and submitted. <span style={ { color: 'green' } }>Thank you.</span></p>
                    </div>
                )
            case AuditReviewStatus.NOT_SUBMITTED:
                return (
                    <div className='new-info-row'>
                        { sessionContext.getNotSubmittedText ( yr ) }
                    </div>
                )
            case AuditReviewStatus.REQUIRES_REVIEW:
                return (
                    <div className='new-info-row'>
                        <p style={ { textAlign: 'center' } }>This audit report is for the academic year { academicYearRangeStr }.
                            The report has been completed and submitted but is now due for a periodic review.
                            <span style={ { color: 'red' } }>&nbsp;Please click the REOPEN AUDIT button below to start this process.</span></p>
                    </div>
                )
            case AuditReviewStatus.IN_REVIEW:
                return (
                    <div className='new-info-row'>
                        <p style={ { textAlign: 'center' } }>This audit report is for the academic year { academicYearRangeStr }.
                            You are currently reviewing this audit. Please review the sections below, paying particular attention to any AMBER or RED
                            ratings from last time.
                            When complete, you can submit this report by clicking the submit button below.</p>
                    </div>
                )
            case AuditReviewStatus.REVIEW_SUBMITTED:

                const submitted_date = instance.dmy_review_completed_date;

                return (
                    <div className='new-info-row'>
                        <p style={ { textAlign: 'center' } }>This audit report is for the academic year { academicYearRangeStr }.
                            The report has been reviewed and was re-submitted on { submitted_date }. <span
                                style={ { color: 'green' } }>Thank you.</span></p>
                    </div>
                )
        }
    }

    const areAnySectionsIncomplete = () =>
    {
        return data.data[ 0 ].sections.reduce ( ( cur, rec ) =>
        {
            return +( rec.section_status ) !== 2 || cur === true ? true : cur;
        }, false );
    }

    const areAnySectionsInReview = () =>
    {
        return data.data[ 0 ].sections.reduce ( ( cur, rec ) =>
        {
            return +( rec.in_review ) !== 0 || cur === true ? true : cur;
        }, false );
    }

    const submitButton = () =>
    {
        const reviewStatus = getReviewStatus ();

        switch ( reviewStatus )
        {
            case AuditReviewStatus.SUBMITTED:
            case AuditReviewStatus.REVIEW_SUBMITTED:

                if ( isFeatureEnabled ( ApplicationFeatures.ADHOC_REOPEN ) )
                {
                    return <ContainerButton onClick={ onClickUpload } title='Reopen Audit for Review'/>
                }
                else
                {
                    return <ContainerButton disabled title='Audit Complete'/>
                }

            case AuditReviewStatus.NOT_SUBMITTED:
                if ( areAnySectionsIncomplete () )
                {
                    return <ContainerButton disabled onClick={ onClickUpload } title='You can submit when the survey is complete'/>
                }
                else
                {
                    return <ContainerButton onClick={ onClickUpload } title='Complete and Submit the Audit'/>
                }

            case AuditReviewStatus.IN_REVIEW:
                if ( areAnySectionsInReview () )
                {
                    return <ContainerButton disabled onClick={ onClickUpload } title='You can submit when the review is complete'/>
                }
                else
                {
                    return <ContainerButton onClick={ onClickUpload } title='Complete and Submit the Audit'/>
                }
            case AuditReviewStatus.REQUIRES_REVIEW:
                return <ContainerButton onClick={ onClickUpload } title='Review Due - Reopen Audit'/>
        }

    }

    const sectionList = () =>
    {
        if ( data.isLoading )
        {
            return <SpinnerDiv/>
        }
        else
        {
            const reviewStatus = getReviewStatus ();
            const sections = data.data[ 0 ].sections;

            return (
                sections.map ( ( rec ) =>
                {
                    return <SectionLink key={ rec.table_section_id } rec={ rec } provider_id={ encodeURIComponent ( provider_id ) }
                                        year={ academic_year } section={ rec.table_section_id } section_ref={ rec.section_ref }
                                        name={ rec.table_section_name } provider_table_section_id={ rec.provider_table_section_id }
                                        status={ rec.section_status } review_status={ reviewStatus } text={ rec.section_subtitle }/>
                } ) )
        }

    }

    return (
        <Fragment>
            <div className='container-for-tabs'>
                { data.isLoading === false && sectionHeading () }
                <div className='sections-grid-rows'>
                    { sectionList () }
                </div>
                { data.isLoading === false && submitButton () }
                { renderModal () }
            </div>
        </Fragment>
    )
}

export default SectionPageSurvey;
